import { PlannedOrder } from "@/reducers/plannedOrdersSlice"
import { RouteStop } from "@/reducers/routes2gSlice"

const hasCoordinates = (stop: RouteStop): boolean => {
  const lat = Number(stop.location.lat)
  const lon = Number(stop.location.lon)
  return !Number.isNaN(lat) && !Number.isNaN(lon) && (lat !== 0 || lon !== 0)
}

const geocodeAddress = async (
  address: string,
): Promise<{ lat: string; lon: string } | null> => {
  if (typeof google === "undefined" || !google.maps?.Geocoder) return null
  const geocoder = new google.maps.Geocoder()
  try {
    const { results } = await geocoder.geocode({ address })
    const location = results[0]?.geometry.location
    if (!location) return null
    return { lat: location.lat().toString(), lon: location.lng().toString() }
  } catch {
    return null
  }
}

// Manually entered planned-order stops have no coordinates, which the vehicle
// suggestions need for distance ranking. Resolve them lazily via the Google
// geocoder already loaded for the maps. Returns the enriched order, or null
// when nothing changed (all stops resolved / geocoder unavailable / no hits).
export const geocodePlannedOrderStops = async (
  order: PlannedOrder,
): Promise<PlannedOrder | null> => {
  let changed = false
  const stops = await Promise.all(
    order.stops.map(async (stop) => {
      if (hasCoordinates(stop)) return stop
      const address = [stop.location.formattedAddress, stop.location.locality]
        .filter(Boolean)
        .join(", ")
      if (!address) return stop
      const coordinates = await geocodeAddress(address)
      if (!coordinates) return stop
      changed = true
      return { ...stop, location: { ...stop.location, ...coordinates } }
    }),
  )
  return changed ? { ...order, stops } : null
}
